import { type FC, Fragment, type ReactNode } from "react";
import { Link } from "react-router";

import { clsx } from "clsx";

import { type CaseStatusTone, CaseStepDot } from "./case_steps";

// The cases list: one card per case, stacked.
export const CaseList: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="flex flex-col gap-3.5">{children}</div>
);

// One case: who it's for, its name with its facts after it on one line, and
// its steps beneath, with its actions at the right. The facts give way to an
// ellipsis before the name does. An empty case (no files yet) is a faint
// dashed card.
export const CaseCard: FC<{
  avatar: ReactNode;
  title: ReactNode;
  facts: ReactNode[];
  status: ReactNode;
  actions: ReactNode;
  empty?: boolean;
}> = ({ avatar, title, facts, status, actions, empty }) => (
  <div
    className={clsx(
      "flex flex-wrap items-center gap-x-5 gap-y-3 rounded-[18px]",
      empty
        ? "border-[1.5px] border-dashed border-[rgba(18,53,91,0.2)] bg-white/45 px-[18.5px] py-[14.5px]"
        : "surface-resting px-5 py-4",
    )}
  >
    <div className="flex min-w-[15rem] flex-1 items-start gap-4">
      {avatar}
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-baseline">
          <span className="max-w-full shrink-0 truncate">{title}</span>
          <span className="text-muted min-w-0 truncate text-[12.5px]">
            {facts.map((fact, i) => (
              <Fragment key={i}>
                <span className="mx-1.5 text-slate-300">·</span>
                {fact}
              </Fragment>
            ))}
          </span>
        </div>
        {status && <div className="mt-2.5">{status}</div>}
      </div>
    </div>
    <div className="ml-auto flex items-center justify-end gap-2">{actions}</div>
  </div>
);

export type CaseStep = {
  to: string;
  tone: CaseStatusTone;
  title: string;
  label: string;
};

const CASE_STEP_LABEL: Record<CaseStatusTone, string> = {
  done: "text-body",
  todo: "text-[#687484]",
  working: "font-semibold text-indigo-700",
  failed: "font-semibold text-rose-700",
};

// The line into a step turns teal once the step before is done and this one
// is done or under way.
const connectorLit = (previous: CaseStatusTone, tone: CaseStatusTone) =>
  previous === "done" && (tone === "done" || tone === "working");

// The case's steps as one fixed strip of dots, each over its label and
// linking to its tab; `title` spells the state out on hover and for screen
// readers. The dots sit at equal spacing, so they line up down the list: the
// first at the strip's left edge (its label starts under the name), the last
// at its right, with the lines running dot to dot. When the strip gets too
// narrow the labels drop.
export const CaseStepper: FC<{ steps: CaseStep[] }> = ({ steps }) => {
  const last = steps.length - 1;
  const many = steps.length > 3;

  return (
    <div className="@container w-[380px] max-md:w-full">
      <ol
        aria-label="Case steps"
        className={clsx(
          "relative h-[33px]",
          many ? "@max-[15rem]:h-4" : "@max-[9rem]:h-4",
        )}
      >
        <span aria-hidden className="absolute inset-x-2 top-[7px] flex h-0.5">
          {steps.slice(1).map((step, i) => (
            <span
              key={step.to}
              className={clsx(
                "flex-1",
                connectorLit(steps[i].tone, step.tone)
                  ? "bg-indigo-600"
                  : "bg-[#e3e8ee]",
              )}
            />
          ))}
        </span>
        {steps.map((step, i) => {
          const first = i === 0;
          const final = i === last;
          const middle = !first && !final;

          return (
            <li
              key={step.to}
              className={clsx(
                "absolute top-0",
                first && "left-0",
                final && "right-0",
                middle && "-translate-x-1/2",
              )}
              style={
                middle
                  ? { left: `calc(0.5rem + ${i / last} * (100% - 1rem))` }
                  : undefined
              }
            >
              <Link
                to={step.to}
                title={step.title}
                aria-label={step.title}
                className={clsx(
                  "group flex flex-col gap-1",
                  first && "items-start",
                  final && "items-end",
                  middle && "items-center",
                )}
              >
                <CaseStepDot tone={step.tone} />
                <span
                  className={clsx(
                    "text-[11px] leading-[14px] whitespace-nowrap group-hover:underline",
                    many ? "@max-[15rem]:hidden" : "@max-[9rem]:hidden",
                    CASE_STEP_LABEL[step.tone],
                  )}
                >
                  {step.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

// The row's ••• menu, bare beside the next-step button.
export const CaseCardMore: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="button-bare">{children}</div>
);
